
import React from 'react';
import { Customer } from '../types';

interface CustomerTableProps {
  customers: Customer[];
  onSelect: (customer: Customer) => void;
}

const CustomerTable: React.FC<CustomerTableProps> = ({ customers, onSelect }) => {
  const safeCustomers = Array.isArray(customers) ? customers : [];

  const formatCurrency = (value: unknown) => {
    const amount = Number(value || 0);
    return "$" + (Number.isFinite(amount) ? amount.toLocaleString() : "0");
  };

  const getCustomerLtv = (customer: Customer) => {
    const customerData = customer as Customer & { mrr?: number | string; ltv?: number | string };
    return customerData.totalLtv ?? customerData.ltv ?? customerData.mrr ?? 0;
  };

  const getRiskStyle = (level: string) => {
    switch (level) {
      case 'High': return 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'Medium': return 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
      default: return 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Identity</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Risk Score</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Location</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Last Seen</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Life LTV</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {safeCustomers.length > 0 ? (
              safeCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all cursor-pointer group" onClick={() => onSelect(customer)}>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                       <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-black text-xs">
                          {customer.name.charAt(0)}
                       </div>
                       <div className="flex flex-col">
                         <span className="text-sm font-black text-slate-900 dark:text-white leading-tight">{customer.name}</span>
                         <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{customer.id}</span>
                       </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2">
                       <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border flex items-center gap-1.5 ${getRiskStyle(customer.riskLevel)}`}>
                         {customer.riskLevel === 'High' && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span>}
                         {customer.riskLevel} Churn
                       </span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-xs text-slate-600 dark:text-slate-300 font-bold">{customer.location}</td>
                  <td className="px-8 py-5 text-xs text-slate-500 dark:text-slate-400 font-bold">
                    {new Date(customer.lastActive).toLocaleDateString()}
                  </td>
                  <td className="px-8 py-5 text-right font-black text-slate-900 dark:text-white">{formatCurrency(getCustomerLtv(customer))}</td>
                  <td className="px-8 py-5 text-right">
                    <button className="text-slate-300 group-hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/50" onClick={(e) => { e.stopPropagation(); onSelect(customer); }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-8 py-10 text-center text-slate-400 text-sm font-medium">
                  No customers found or data is loading.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerTable;
